import React, { useCallback, useMemo, useState } from "react";
import { partition, sortBy } from "@common/helpers/collections";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import {
    FinancialAccount,
    FinancialAccountType,
} from "@common/types/financialAccount";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useUser } from "@hooks/useUser";
import { useEffectOnce } from "@hooks/useEffectOnce";
import { Button, type ButtonSize } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import { getUnassignedFinancialAccounts } from "@lib/financialAccount";
import { unassignedAccountsQueryKey } from "@queries/unassignedAccounts";
import { trackEvent } from "@lib/analytics";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { PlusIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { AccountsConnectionOnboardingWidget } from "../../../financialAccount/AccountConnectionWidget/AccountsConnectionOnboardingWidget";
import { AccountsConnectionProvider } from "../../../financialAccount/AccountConnectionWidget/AccountsConnectionProvider";
import { ConnectPlaidAccountProps } from "../../../plaid/ConnectPlaid/ConnectPlaid";
import { Footer } from "../../components/Footer";
import { PersonalDataAlert } from "../../components/PersonalDataAlert";
import commonStyles from "../common.module.scss";
import animations from "../../layout/OnboardingLayout.module.scss";
import { useConnectAccount } from "../../../connectAccount/ConnectAccount.context";
import { CommandsProvider } from "../../../commands/CommandsProvider";

interface ConnectAccountsProps extends Pick<
    ConnectPlaidAccountProps,
    "onConnected" | "defaultEntity" | "beforeConnect"
> {
    onBack?(): void;
    onFinished(): void;
    allowNoAccounts?: boolean;
    buttonSize?: Exclude<ButtonSize, "xs">;
}

type IntegrationAccountWithFinancialAccount = IntegrationAccount & {
    financialAccount: FinancialAccount;
};

function splitAccountsByType(
    allAccounts: IntegrationAccountWithFinancialAccount[],
) {
    const [checkingAccounts, creditCards] = partition(
        allAccounts,
        (account) =>
            account.financialAccount.financialType !==
            FinancialAccountType.CREDIT_CARD,
    );

    return { checkingAccounts, creditCards };
}

export const ConnectAccounts: React.FC<ConnectAccountsProps> = ({
    onBack,
    onFinished,
    allowNoAccounts,
    defaultEntity,
    beforeConnect,
    buttonSize = "md",
}) => {
    const {
        state: { isConnecting, connection: newConnection },
    } = useConnectAccount();

    const user = useUser();
    // when onboarding additional entity, show only accounts from connections created during the flow
    // (entity is automatically assigned to new accounts created during additional entity onboarding flow)
    const allFinancialAccounts = useFinancialAccounts({
        entityId: defaultEntity?.id,
    });
    // when performing initial onboarding, entity is not assigned by default to account, so need to fetch all unassigned accounts
    const unassignedAccounts = useQuery({
        queryKey: [unassignedAccountsQueryKey],
        queryFn: () =>
            user.onboardingComplete ? [] : getUnassignedFinancialAccounts(),
    });

    const [showAddAccount, setShowAddAccount] = useState<boolean>(false);
    const [busy, setBusy] = useState<boolean>(false);

    const allFinancialAccountsOrdered = useMemo(
        () =>
            sortBy(
                [...allFinancialAccounts, ...(unassignedAccounts.data ?? [])],
                "id",
            ).flatMap((a) =>
                (a.integrationAccounts ?? []).map((ia) => ({
                    ...ia,
                    financialAccount: a,
                })),
            ),
        [allFinancialAccounts, unassignedAccounts],
    );

    const hasAccountWithNoType = allFinancialAccounts.some(
        (acc) => acc.isBusiness === null,
    );

    const { checkingAccounts, creditCards } = useMemo(
        () => splitAccountsByType(allFinancialAccountsOrdered),
        [allFinancialAccountsOrdered],
    );

    useEffectOnce(() => {
        void trackEvent(AnalyticsEvent.ONBOARDING_STEP_CONNECT_ACCOUNTS, {
            bankAccountsConnected: checkingAccounts.length,
            creditCardsConnected: creditCards.length,
        });
    });

    const handleAccountConnecting = useCallback(() => {
        setBusy(true);
    }, []);

    const handleAccountConnected = useCallback(() => {
        setBusy(false);
        setShowAddAccount(false);
    }, []);

    const handleCommand = useCallback(async () => {
        if (busy) {
            return;
        }
        await beforeConnect?.();
        setShowAddAccount(true);
    }, [beforeConnect, busy]);

    const finish = useCallback(() => {
        onFinished();
    }, [onFinished]);

    const preventContinue =
        busy ||
        isConnecting ||
        !!newConnection ||
        (!allFinancialAccounts.length && !allowNoAccounts) ||
        hasAccountWithNoType;

    useKeyboardCommand({
        keys: "a",
        callback: () => handleCommand(),
        options: {
            description: "Open add accounts portal",
            preventDefault: !busy,
        },
    });

    return (
        <AccountsConnectionProvider
            showAccountTypeWarning
            defaultEntity={defaultEntity}
        >
            <CommandsProvider useDefaultKeyboardCommand={false}>
                <div
                    className={classNames(
                        animations.fadeIn1,
                        commonStyles.content780,
                    )}
                >
                    <h1
                        className={classNames(
                            commonStyles.title,
                            animations.fadeIn2,
                        )}
                    >
                        Connect your bank accounts and credit cards
                    </h1>

                    <PersonalDataAlert />
                    <section>
                        <AccountsConnectionOnboardingWidget
                            heading={<h5>Bank accounts</h5>}
                            emptyConnectPrompt={
                                <HStack gap={8} align="center">
                                    <PlusIcon /> Add bank accounts
                                    <Shortcut keys="a" />
                                </HStack>
                            }
                            connectPrompt={
                                <HStack gap={8} align="center">
                                    Add another account
                                    <Shortcut keys="a" />
                                </HStack>
                            }
                            accounts={checkingAccounts}
                            showAddAccount={showAddAccount}
                            beforeConnect={beforeConnect}
                            onAddAccount={handleAccountConnecting}
                            onAddAccountComplete={handleAccountConnected}
                            defaultEntity={defaultEntity}
                            buttonSize={buttonSize}
                        />
                    </section>

                    <section className="mt-5">
                        <AccountsConnectionOnboardingWidget
                            heading={<h5>Credit cards</h5>}
                            emptyConnectPrompt={
                                <HStack gap={8} align="center">
                                    <PlusIcon /> Add credit cards
                                    <Shortcut keys="a" />
                                </HStack>
                            }
                            connectPrompt={
                                <HStack gap={8} align="center">
                                    Add another card
                                    <Shortcut keys="a" />
                                </HStack>
                            }
                            accounts={creditCards}
                            showAddAccount={showAddAccount}
                            beforeConnect={beforeConnect}
                            onAddAccount={handleAccountConnecting}
                            onAddAccountComplete={handleAccountConnected}
                            defaultEntity={defaultEntity}
                            buttonSize={buttonSize}
                        />
                    </section>

                    <Footer onBack={onBack} onBackDisabled={busy}>
                        <WithHotkey
                            hotKey="enter"
                            hotKeyOptions={{
                                description: "Continue",
                                enabled: !preventContinue,
                            }}
                            hotKeyDisplayPosition="inline"
                        >
                            {(hotkeyHint) => (
                                <Button
                                    variant="primary"
                                    disabled={preventContinue}
                                    onClick={finish}
                                    size="xl"
                                    rightIcon={hotkeyHint}
                                >
                                    {!preventContinue &&
                                    !allFinancialAccounts.length
                                        ? "Skip for now"
                                        : "Next"}
                                </Button>
                            )}
                        </WithHotkey>
                    </Footer>
                </div>
            </CommandsProvider>
        </AccountsConnectionProvider>
    );
};
